import React, {useContext, useEffect, useState} from 'react';
import {
    Card, CardContent, Divider,
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
    TextField, Stack, CardHeader,
    Checkbox, Menu, MenuItem, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, Typography, Grid, IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import { api } from "../../../services/Main/Api";
import { formatCpfCnpj, formatPhone, stripNonDigits } from "../../../helpers/formatters";
import { getCondoId } from "../../../services/Main/AuthStorage";
import axios from "axios";
import SearchIcon from '@mui/icons-material/Search';
import {useSnackbar} from "notistack";

import PaginationPage from "../../../components/PaginationPage/PaginationPage";
import moment from "moment";

const states = [
    'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
    'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
];

const CustomerList = () => {
    const [search, setSearch] = useState('');
    const [customers, setCustomers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [page, setPage] = useState(1);
    const [showSearch, setShowSearch] = useState(false);
    const {enqueueSnackbar} = useSnackbar()
    const [newClient, setNewClient] = useState({
        name: '', cpf: '', email: '', phone: '',
        street: '', number: '', complement: '',
        district: '', city: '', state: '', cep: '',
        condo: getCondoId(),
    });

    const openMenu = Boolean(anchorEl);

    useEffect(() => {
        const filters = {
            search,
            page: page,
            page_size: 50,
            condo_id: getCondoId(),
        };

        api.get('unit/', { params: filters })
            .then(response => {
                setCustomers(response.data)
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setSelectedIds([])
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [search, page]);

    const handleSelectAll = (event) => {
        const results = customers?.results || [];
        setSelectedIds(event.target.checked ? results.map((c) => c.id) : []);
    };

    const handleSelectOne = (id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]);
    };

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleDeleteClick = () => { setConfirmOpen(true); handleMenuClose(); };
    const handleDeleteConfirmed = async () => {
        try {
            await Promise.all(
                selectedIds.map((id) => api.delete(`unit/${id}/`))
            );

            const updated = customers.results.filter((u) => !selectedIds.includes(u.id));
            setCustomers((prev) => ({ ...prev, results: updated }));
            enqueueSnackbar("Cliente foi deletado com sucesso!", {variant: 'success'});
        } catch (error) {
            console.error('Erro ao deletar cliente:', error);
            enqueueSnackbar(`Erro ao deletar cliente:${error}`, {variant: 'error'});
            // Aqui você pode adicionar um snackbar ou alerta visual se quiser
        } finally {
            setSelectedIds([]);
            setConfirmOpen(false);
        }
    };

    const handleRowClick = (cliente) => {
        setNewClient(cliente);
        setEditId(cliente.id);
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleDialogChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value;
        if (name === 'cpf') formattedValue = formatCpfCnpj(value);
        if (name === 'phone') formattedValue = formatPhone(value);
        setNewClient((prev) => ({ ...prev, [name]: formattedValue }));
    };

    const validateFields = () => {
        const required = ['name', 'cpf', 'street', 'number', 'cep', 'district', 'city', 'state'];
        const newErrors = {};
        required.forEach((field) => {
            if (!newClient[field]?.trim()) newErrors[field] = 'Campo obrigatório';
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveClient = () => {
        if (!validateFields()) return;

        const payload = {
            ...newClient,
            cpf: stripNonDigits(newClient.cpf),
            phone: stripNonDigits(newClient.phone),
            cep: stripNonDigits(newClient.cep),
        };

        if (editMode) {
            api.put(`unit/${editId}/`, payload)
                .then(response => {
                    const updatedResults = customers.results.map(c => c.id === editId ? response.data : c);
                    setCustomers({ ...customers, results: updatedResults });
                    setDialogOpen(false);
                    setNewClient({ name: '', cpf: '', email: '', phone: '', street: '', number: '', cep: '', district: '', city: '', state: '', complement: '', condo: getCondoId() });
                    setErrors({});
                    setEditMode(false);
                    enqueueSnackbar("Cliente foi atualizado com sucesso!", {variant: 'success'});
                });
        } else {
            api.post('unit/', payload)
                .then((response) => {
                    setCustomers((prev) => ({
                        ...prev,
                        results: [...(prev.results || []), response.data]
                    }));
                    setDialogOpen(false);
                    setNewClient({ name: '', cpf: '', email: '', phone: '', street: '', number: '', cep: '', district: '', city: '', state: '', complement: '', condo: getCondoId() });
                    setErrors({});
                    enqueueSnackbar("Cliente foi criado com sucesso!", {variant: 'success'});
                });
        }
    };

    const isSelected = (id) => selectedIds.includes(id);
    const allSelected = customers.results && customers.results.length > 0 && selectedIds.length === customers.results.length;

    const handleCepBlur = () => {
        const rawCep = newClient.cep?.replace(/\D/g, '');

        if (rawCep?.length !== 8) return;

        axios.get(`https://opencep.com/v1/${rawCep}.json`)
            .then(response => {
                setNewClient(prev => ({
                    ...prev,
                    street: response.data.logradouro || '',
                    district: response.data.bairro || '',
                    city: response.data.localidade || '',
                    state: response.data.uf || ''
                }));
            })
            .catch(error => {
                console.error('Erro ao buscar endereço pelo CEP:', error);
            });
    };

    return (
        <>
            <Card sx={{ margin: 2 }}>
                <CardHeader
                    title={`Clientes (${customers?.count})`}

                />
                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" sx={{px:2}} spacing={2} alignItems="center">
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setDialogOpen(true);
                            setEditMode(false);
                            setNewClient({ name: '', cpf: '', email: '', phone: '', street: '', number: '', complement: '', district: '', city: '', state: '', cep: '', condo: getCondoId() });
                        }}
                    >
                        Criar Cliente
                    </Button>
                    {showSearch ? (
                        <TextField
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Pesquisar..."
                            autoFocus
                            onBlur={() => {
                                if (!search.trim()) {
                                    setShowSearch(false);
                                }
                            }}
                            InputProps={{
                                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
                            }}
                        />
                    ) : (
                        <IconButton onClick={() => setShowSearch(true)}>
                            <SearchIcon />
                        </IconButton>
                    )}


                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<KeyboardArrowDownIcon />}
                                onClick={handleMenuClick}
                                disabled={selectedIds.length === 0}
                            >
                                Ações {selectedIds.length > 1 ? `(${selectedIds.length} itens)` : selectedIds.length === 1 ? `(${selectedIds.length} item)` : null}
                            </Button>
                            <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
                                <MenuItem onClick={handleDeleteClick}>
                                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                                    Excluir selecionados
                                </MenuItem>
                            </Menu>
                        </>


                </Stack>

                <CardContent>
                    {loading ? (
                        <Typography variant="body2">Carregando clientes...</Typography>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={allSelected} onChange={handleSelectAll} />
                                        </TableCell>
                                        <TableCell><strong>Nome</strong></TableCell>
                                        <TableCell><strong>CPF/CNPJ</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Telefone</strong></TableCell>
                                        <TableCell><strong>Criado em</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {customers?.results?.map((cliente) => (
                                        <TableRow key={cliente.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(cliente)}>
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox checked={isSelected(cliente.id)} onChange={() => handleSelectOne(cliente.id)} />
                                            </TableCell>
                                            <TableCell>{cliente.name}</TableCell>
                                            <TableCell>{formatCpfCnpj(cliente.cpf)}</TableCell>
                                            <TableCell>{cliente.email}</TableCell>
                                            <TableCell>{formatPhone(cliente.phone)}</TableCell>
                                            <TableCell>{moment(cliente.created_at).format("DD/MM/YYYY [às] HH:mm")}</TableCell>
                                        </TableRow>
                                    ))}
                                    {customers.results?.length === 0 && (
                                        <TableRow><TableCell colSpan={5} align="center">Nenhum cliente encontrado.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
            <PaginationPage
                data={{ ...customers, page }}
                            onChange={(newPage) => setPage(newPage)} />
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja deletar {selectedIds.length} {selectedIds.length === 1 ? 'item' : 'itens'}?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} size="small">Cancelar</Button>
                    <Button onClick={handleDeleteConfirmed} color="error" variant="contained" size="small">Deletar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{editMode ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={2} mt={1}>
                        {/* Campos principais */}
                        {['name', 'cpf', 'email', 'phone'].map((field) => {
                            const labelMap = {
                                name: 'Nome', cpf: 'CPF ou CNPJ', email: 'Email', phone: 'Telefone'
                            };
                            return (
                                <Grid item xs={12} sm={6} key={field}>
                                    <TextField
                                        label={labelMap[field]}
                                        name={field}
                                        value={newClient[field] || ''}
                                        onChange={handleDialogChange}
                                        fullWidth
                                        size="small"
                                        error={!!errors[field]}
                                        helperText={errors[field]}
                                    />
                                </Grid>
                            );
                        })}

                        {/* Título do grupo de endereço */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Endereço
                            </Typography>
                        </Grid>

                        {/* Campo de CEP com onBlur para buscar endereço */}
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="CEP"
                                name="cep"

                                value={newClient.cep || ''}
                                onChange={(e) => {
                                    const cep = e.target.value.replace(/\D/g, '');
                                    if (cep.length <= 8) {
                                        setNewClient((prev) => ({
                                            ...prev,
                                            cep: cep,
                                        }))
                                    }

                                    }
                                }
                                onBlur={() => {
                                    const cep = newClient.cep?.replace(/\D/g, '');
                                    if (cep?.length === 8) {
                                        api.get(`https://viacep.com.br/ws/${cep}/json/`)
                                            .then((response) => {
                                                setNewClient((prev) => ({
                                                    ...prev,
                                                    street: response.data.logradouro || '',
                                                    district: response.data.bairro || '',
                                                    city: response.data.localidade || '',
                                                    state: response.data.uf || '',
                                                }));
                                            })
                                            .catch(console.error);
                                    }
                                }}
                                fullWidth
                                size="small"
                                error={!!errors.cep}
                                helperText={errors.cep}
                            />
                        </Grid>

                        {/* Demais campos do endereço */}
                        {['street', 'number', 'district', 'city', 'state', 'complement'].map((field) => {
                            const isSelect = field === 'state';
                            const labelMap = {
                                street: 'Rua', number: 'Número', district: 'Bairro',
                                city: 'Cidade', state: 'UF', complement: 'Complemento'
                            };
                            const columnSize = field === 'number' ? 3 : field === 'complement' ? 12 : field === 'city' ? 4 : field === 'state' ? 2 : 6;
                            return (
                                <Grid item xs={12} sm={columnSize} key={field}>
                                    <TextField
                                        select={isSelect}
                                        label={labelMap[field]}
                                        name={field}
                                        value={newClient[field] || ''}
                                        onChange={handleDialogChange}
                                        fullWidth
                                        size="small"
                                        error={!!errors[field]}
                                        helperText={errors[field]}
                                    >
                                        {isSelect && states.map((uf) => (
                                            <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            );
                        })}
                    </Grid>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ py: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveClient} variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>



        </>
    );
};

export default CustomerList;
